import {observer} from "mobx-react";
import React from "react";

import {AddButton, SaveButton} from "../../atoms/buttons/buttons";
import {
    ActionsColumn,
    FieldsColumn,
    HeaderGrid,
    InputWidthWrapper,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {ErrorText, FormField} from "../../molecules/inputs/form-field";
import {SubmitGroup} from "../../molecules/submit-group/submit-group";
import {CategoryRow} from "../../molecules/tables/category-row";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {DeleteDialog} from "../../organisms/delete-dialog/delete-dialog";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {CategoryGroupEditPageStore} from "./category-group-edit-page-store";

const columns = [
    {label: "カテゴリ名"},
    {label: "識別子（Slug）", width: 200},
    {label: "関連ドキュメント", width: 150, required: false},
    {label: "", width: 60, required: false},
];

export const CategoryGroupEditPage: React.FC<{pageStore: CategoryGroupEditPageStore}> = observer(
    (props) => {
        const {
            categoryGroup,
            categoryGroupId,
            tableRows,
            groupLabel,
            groupSlug,
            groupLabelError,
            groupSlugError,
            canUpdate,
            duplicateError,
            addCategory,
            updateCategoryGroup,
            confrmDeleteCategory,
            removeCategory,
            isPersistedId,
            setGroupLabel,
            setGroupSlug,
            setLabel,
            setSlug,
        } = props.pageStore;
        return (
            <AppLayout
                breadcrumb={
                    <AdminBreadcrumb
                        factory={(builder) =>
                            builder
                                .categoryGroups()
                                .categoryGroupsDetail(categoryGroup.label, categoryGroupId)!
                                .custom("編集")
                                .build()
                        }
                    />
                }
                buttons={<></>}
            >
                <SubmitGroup
                    onSubmit={async () => {
                        await updateCategoryGroup();
                    }}
                    canSubmit={canUpdate}
                >
                    <PageContainer>
                        {
                            /* エラーメッセージの表示 */
                            duplicateError && <ErrorText>{duplicateError}</ErrorText>
                        }
                        {/* ヘッダー部分 */}
                        <HeaderGrid>
                            {/* 左カラム：ラベル付きフォームを縦に並べる */}
                            <FieldsColumn aria-label="カテゴリグループの入力フィールド">
                                <InputWidthWrapper>
                                    <FormField
                                        label="カテゴリグループ名"
                                        required
                                        placeholder="グループ名を入力"
                                        value={groupLabel}
                                        onChange={(e) => setGroupLabel(e.currentTarget.value)}
                                        onBlur={(e) => setGroupLabel(e.currentTarget.value)}
                                        errorText={groupLabelError}
                                    />
                                </InputWidthWrapper>

                                <InputWidthWrapper>
                                    <FormField
                                        label="識別子（Slug）"
                                        required
                                        placeholder="コードを入力"
                                        value={groupSlug}
                                        onChange={(e) => setGroupSlug(e.currentTarget.value)}
                                        onBlur={(e) => setGroupSlug(e.currentTarget.value)}
                                        errorText={groupSlugError}
                                    />
                                </InputWidthWrapper>
                            </FieldsColumn>

                            {/* 右カラム：アクションボタン（下端揃え） */}
                            <ActionsColumn>
                                <AddButton onClick={() => addCategory()}>カテゴリの追加</AddButton>
                                <SaveButton disabled={!canUpdate}>保存</SaveButton>
                            </ActionsColumn>
                        </HeaderGrid>

                        {/* テーブル部分 */}
                        <TableLayout areaLabel="カテゴリ編集" columns={columns}>
                            {tableRows.map(
                                ({label, slug, categoryId, labelError, slugError, count}) => {
                                    return (
                                        <CategoryRow
                                            key={categoryId}
                                            id={categoryId}
                                            label={label}
                                            slug={slug}
                                            labelError={labelError}
                                            slugError={slugError}
                                            canRemove={tableRows.length > 1}
                                            count={Number(count ?? 0)}
                                            onChangeLabel={(id, newValue) => setLabel(id, newValue)}
                                            onChangeSlug={(id, newValue) => setSlug(id, newValue)}
                                            onRemove={() => {
                                                if (isPersistedId(categoryId)) {
                                                    confrmDeleteCategory(categoryId, label, count!);
                                                } else {
                                                    // 数字ではないIDは一時的に追加された（UUID）と判断
                                                    // 空のフォーム行なので確認モーダルを表示せずそのまま削除
                                                    removeCategory(categoryId);
                                                }
                                            }}
                                        />
                                    );
                                },
                            )}
                        </TableLayout>
                    </PageContainer>
                </SubmitGroup>
                <DeleteDialog />
            </AppLayout>
        );
    },
);
